// client/src/components/SmartText.js
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

// ✅ Fix common API issues:
// - Unbalanced math delimiters like "$y \\le 3$$"
// - Converts \( \) and \[ \] into $ and $$
// - Normalizes unicode minus to normal minus
const normalizeLatexDelimiters = (input) => {
  if (input == null) return "";
  let out = String(input);

  // Convert \( \) and \[ \] to $ / $$
  out = out.replace(/\\\(/g, "$").replace(/\\\)/g, "$");
  out = out.replace(/\\\[/g, "$$").replace(/\\\]/g, "$$");

  // Common unicode cleanup
  out = out.replace(/\u2212/g, "-"); // minus sign → hyphen
  out = out.replace(/\u00A0/g, " "); // nbsp → space

  // Fix odd number of $ (most common bad case: "$...$$")
  const dollarCount = (out.match(/\$/g) || []).length;
  if (dollarCount % 2 === 1) {
    // remove one trailing $ first if present, otherwise remove one leading $
    if (/\$\s*$/.test(out)) out = out.replace(/\$\s*$/, "");
    else if (/^\s*\$/.test(out)) out = out.replace(/^\s*\$/, "");
  }

  // Normalize common plain-math tokens from AI output.
  // e.g. "sqrt(14)" -> "\sqrt{14}"
  out = out.replace(/\bsqrt\(([^)]+)\)/g, "\\sqrt{$1}");

  // If this looks like a standalone math expression but has no delimiters,
  // wrap it so remark-math + KaTeX can render it.
  // Guard: do NOT auto-wrap full natural-language sentences with embedded math.
  const hasMathDelimiter = /\$/.test(out);
  const strippedForLanguageCheck = out.replace(
    /\\(frac|sqrt|pm|times|div|cdot|left|right|geq|leq|neq)/g,
    ""
  );
  const hasNaturalLanguage = /[A-Za-z]{4,}/.test(strippedForLanguageCheck);
  const looksMathLike =
    /\\frac|\\sqrt|\\pm|\^|_/.test(out) &&
    /^[A-Za-z0-9\s=+\-*/^().,\\{}[\]±]+$/.test(out) &&
    !hasNaturalLanguage;

  if (!hasMathDelimiter && looksMathLike) {
    out = `$${out}$`;
  }

  return out;
};

const SmartText = ({ content = "" }) => {
  const normalized = useMemo(() => normalizeLatexDelimiters(content), [content]);

  return (
    <div
      className="prose max-w-none break-words [overflow-wrap:anywhere] [word-break:break-word] [&_.katex-display]:max-w-full [&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden [&_.katex-display]:my-1 [&_.katex]:max-w-full [&_.katex]:!whitespace-normal [&_.katex]:[overflow-wrap:anywhere] [&_.katex]:[word-break:break-word]"
    >
      <ReactMarkdown
        // ✅ modern react-markdown usage: pass content as children
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
};

export default SmartText;
